/**
 * 63.5 step 4 (ARCHITECTURE §5.13 "63.5, the design"; 62.19 (e), 134): walking by the ground's material, on the action
 * clock's ground (step 6, decision 155: no switch; match/clockPolicy.ts `ground`). The material under the feet (core/groundMaterial.ts: the facet's, with rain turning
 * soil to mud) sets the walk's pace and footing from GROUND_WALK — mud slow and slower in the rain, sand slow and slower
 * uphill, snow slower the deeper it lies, rock sure underfoot but hard to scramble, ice quick but slippery: the walk's
 * velocity closes on the one asked for at the ice's grip, so it is slow to turn and slides to a stop, and it drifts down a
 * slope. The Match reads it for every walker, bot or person alike; the bots' routes read the dry material as a cost.
 * Pure; deterministic math only.
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import type { GroundMaterial } from '../types/ground';
import { GROUND_WALK } from '../constants/ground';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub } from '../math';
import { facetUnder, facetMaterialCached, facetLayerDepth } from './groundMaterial';
import { weatherHere } from './weather';
import { projectToTangent, rightOf } from './surfaceWalk';

/** The ground's footing under a walker: × its pace, × its scramble, its uphill loss, its grip (null: sure) and its slide. */
export interface Footing { material: GroundMaterial; speed: number; scramble: number; uphill: number; grip: number | null; slide: number }

/** The footing under `dir` on planet `planetIndex` at `actionSeconds` (the weather's time). */
export function footingAt(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, dir: Vec3, actionSeconds: number): Footing {
  const planet = state.planets[planetIndex];
  const t = facetUnder(planet, dir);
  const dry = facetMaterialCached(planet, t);
  const w = dry === 'soil' || dry === 'mud' ? weatherHere(state, planetIndex, dir, actionSeconds) : null;
  const wet = w === 'rain' || w === 'storm';
  const material: GroundMaterial = dry === 'soil' && wet ? 'mud' : dry;   // (groundMaterial's rule: rain turns soil to mud)
  const g = GROUND_WALK[material];
  let speed = g.speed;
  if (g.deep !== undefined) speed += (g.deep - g.speed) * facetLayerDepth(planet, t);   // snow: deeper, slower
  if (wet && dry === 'mud' && g.wet !== undefined) speed *= g.wet;                   // mud in the rain: wetter still
  return { material, speed, scramble: g.scramble, uphill: g.uphill ?? 0, grip: g.grip ?? null, slide: g.slide ?? 0 };
}

/** The bots' routes (a cost, no weather): × the pace and the scramble the dry ground under `dir` allows, and its uphill loss. */
export function routeFooting(planet: PlanetState, dir: Vec3): { speed: number; scramble: number; uphill: number } {
  const g = GROUND_WALK[facetMaterialCached(planet, facetUnder(planet, dir))];
  return { speed: g.deep !== undefined ? (g.speed + g.deep) / 2 : g.speed, scramble: g.scramble, uphill: g.uphill ?? 0 };
}

/**
 * The ground's fall at `n` (a tangent vector, down the steepest way, its length the grade), from the ground's height a
 * step `step` (u) off in two directions — what ice slides down.
 */
export function downhillAt(groundAt: (d: Vec3) => number, n: Vec3, step: number): Vec3 {
  const e1 = vecNormalize(projectToTangent(Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 }, n));
  const e2 = rightOf(e1, n);
  const h0 = groundAt(n);
  const r = h0 > 0 ? h0 : 1;
  const at = (e: Vec3) => vecNormalize(vecAdd(n, vecScale(e, step / r)));
  const g1 = (groundAt(at(e1)) - h0) / step, g2 = (groundAt(at(e2)) - h0) / step;
  return vecSub(vecScale(e1, -g1), vecScale(e2, g2));
}

/** Ice drifts only down ground steeper than this grade (the fine relief's hummocks leave a standing person still). */
export const SLIDE_MIN_GRADE = 0.1;

/**
 * Ice: the walk's velocity this tick (a tangent vector, u/s) — the last one, closing on the one asked for (`want`) at
 * `grip` per second, plus a drift down the slope (`downhill` × slide × the walk's speed). Stepping onto ice (no last
 * velocity) it carries on as it was asked.
 */
export function slipStep(last: Vec3 | undefined, want: Vec3, n: Vec3, grip: number, downhill: Vec3, slide: number, walkSpeed: number, dt: number): Vec3 {
  const v0 = last ? vecSub(last, vecScale(n, vecDot(last, n))) : want;   // (into this ground's plane, its length kept)
  const k = Math.min(1, grip * dt);
  const v = vecAdd(v0, vecScale(vecSub(want, v0), k));
  return slide > 0 && vecDot(downhill, downhill) > SLIDE_MIN_GRADE * SLIDE_MIN_GRADE ? vecAdd(v, vecScale(downhill, slide * walkSpeed * dt)) : v;
}

/** A tangent velocity as the walk's inputs along a heading: forward, right (a unit move between them) and the speed. */
export function velocityAsWalk(v: Vec3, heading: Vec3, n: Vec3): { forward: number; right: number; speed: number } {
  const s = Math.sqrt(vecDot(v, v));
  if (s < 1e-9) return { forward: 0, right: 0, speed: 0 };
  const u = vecScale(v, 1 / s);
  return { forward: vecDot(u, heading), right: vecDot(u, rightOf(heading, n)), speed: s };
}
